export const WESTFIELD_POD_ID = "68cec744-34ac-4ec6-af36-0af781e19d04";
export const WESTFIELD_POD_LABEL = "Westfield London";

export const PRICE_PENCE_PER_MINUTE = 25;
export const MIN_MINUTES = 60;
export const MAX_MINUTES = 240;
export const OPEN_HOUR = 8;
export const CLOSE_HOUR = 22;
export const MIN_NOTICE_MINUTES = 0;

/** Dates closed for reservations - Friday 23 to Sunday 25 October 2026. */
export const BLOCKED_DATE_KEYS = ["2026-10-23", "2026-10-24", "2026-10-25"];

export const isDateBlocked = (dateKey: string) => BLOCKED_DATE_KEYS.includes(dateKey);

export const LONDON = "Europe/London";

export const priceForMinutes = (minutes: number) =>
  `£${((minutes * PRICE_PENCE_PER_MINUTE) / 100).toFixed(2)}`;

export const durationOptions = (): number[] => {
  const options: number[] = [];
  for (let m = MIN_MINUTES; m <= MAX_MINUTES; m += 15) options.push(m);
  return options;
};

export const durationLabel = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h} hour${h === 1 ? "" : "s"}`;
  return `${h}h ${m}m`;
};

/** Offset in minutes between London time and UTC for a given instant. */
const londonOffsetMinutes = (date: Date): number => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return (asUTC - date.getTime()) / 60000;
};

/** Convert a London wall-clock date + time ("2026-09-24", 17, 30) to a UTC Date. */
export const londonWallClockToDate = (dateKey: string, hour: number, minute: number): Date => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const guess = new Date(Date.UTC(y, m - 1, d, hour, minute));
  const offset = londonOffsetMinutes(guess);
  return new Date(guess.getTime() - offset * 60000);
};

/** "2026-09-24" for a date, in London time. */
export const londonDateKey = (date: Date): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: LONDON,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return parts;
};

export const londonDateLabel = (date: Date): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);

export const londonShortDateLabel = (date: Date): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);

export const londonTimeLabel = (date: Date): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(":00", "")
    .toUpperCase();

/** Next 14 selectable dates starting tomorrow (London). Today is not bookable. */
export const upcomingDates = (): { key: string; label: string }[] => {
  const out: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= 21 && out.length < 14; i++) {
    const d = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
    const key = londonDateKey(d);
    if (isDateBlocked(key)) continue;
    out.push({ key, label: londonShortDateLabel(d) });
  }
  return out;
};

export interface ReservedSlot {
  start_at: string;
  end_at: string;
}

export const slotIsTaken = (start: Date, minutes: number, reserved: ReservedSlot[]) => {
  const end = new Date(start.getTime() + minutes * 60000);
  return reserved.some((r) => {
    const rs = new Date(r.start_at).getTime();
    const re = new Date(r.end_at).getTime();
    return start.getTime() < re && end.getTime() > rs;
  });
};

/** Every 15-minute start time between opening and closing for a London date. */
export const startTimesForDate = (dateKey: string): Date[] => {
  const times: Date[] = [];
  for (let hour = OPEN_HOUR; hour < CLOSE_HOUR; hour++) {
    for (const minute of [0, 15, 30, 45]) {
      times.push(londonWallClockToDate(dateKey, hour, minute));
    }
  }
  return times;
};

export const endsBeforeClosing = (start: Date, minutes: number, dateKey: string) => {
  const closing = londonWallClockToDate(dateKey, CLOSE_HOUR, 0);
  return new Date(start.getTime() + minutes * 60000).getTime() <= closing.getTime();
};

export const hasEnoughNotice = (start: Date) =>
  start.getTime() - Date.now() >= MIN_NOTICE_MINUTES * 60000;
